import deposit_ABI from "../../contracts/deposits.json";
import { USDC_ADDRESS } from "env";
import { ethers } from "ethers";
import { determineAddress } from "../../common";
import QuoterABI from "../../contracts/QuoterABI.json";
import { AssetType, PrimaryInvestmentType } from "types";
import { PrimaryInvestments } from "constants/constants";

const QUOTER_CONTRACT_ADDRESS = "0x0d5e0f971ed27fbff6c2837bf31316121532048d";
interface PricePerShare {
  buy: string;
  sell: string;
}

export async function getPriceOfTokenByQuantity(co: AssetType, quantity: string): Promise<any> {
  const feeMultiplier = ethers.utils.parseUnits("0.997", 6); // 99.7% as a decimal with 6 decimals

  const provider = new ethers.providers.JsonRpcProvider(
    "https://base-mainnet.infura.io/v3/5b51ad43553b44ffabc2980afa70f7ae"
  );
  const quoterContract = new ethers.Contract(QUOTER_CONTRACT_ADDRESS, QuoterABI, provider);

  const amountToQuote = ethers.utils.parseUnits(quantity, 6);
  const amountAfterFee = amountToQuote;
  const tokenAddress = determineAddress(co);

  const poolKey = {
    currency0: tokenAddress.toLowerCase() < USDC_ADDRESS.toLowerCase() ? tokenAddress : USDC_ADDRESS,
    currency1: tokenAddress.toLowerCase() < USDC_ADDRESS.toLowerCase() ? USDC_ADDRESS : tokenAddress,
    fee: 3000,
    tickSpacing: 60,
    hooks: "0x0000000000000000000000000000000000000000"
  };
  // Determine if token is token0 or token1
  const tokenIsToken0 = tokenAddress.toLowerCase() === poolKey.currency0.toLowerCase();

  try {
    // For sell quote: we're selling token for USDC (exact input)
    const quoteSellResult = await quoterContract.callStatic.quoteExactInputSingle({
      poolKey: poolKey,
      zeroForOne: tokenIsToken0, // If token is token0, we're going from token0 to token1
      exactAmount: amountAfterFee,
      hookData: "0x"
    });

    const readableSellQuote = ethers.utils.formatUnits(quoteSellResult.amountOut, 6);
    return readableSellQuote;
  } catch (error) {
    console.error("Quantity Quote Error", error);
  }
}

export async function getPricePerShare(co: AssetType): Promise<PricePerShare> {
  //eventually we should probably keep this on the server and return the result
  // so we don't expose this url to the front end TODO
  const provider = new ethers.providers.JsonRpcProvider(
    "https://base-mainnet.infura.io/v3/5b51ad43553b44ffabc2980afa70f7ae"
  );

  const quoterContract = new ethers.Contract(QUOTER_CONTRACT_ADDRESS, QuoterABI, provider);
  const oneShare = ethers.utils.parseUnits("1", 6);
  const objToReturn = { buy: "", sell: "" };

  const feeMultiplier = ethers.utils.parseUnits("0.997", 6); // 99.7% as a decimal with 6 decimals

  // Scale down to maintain precision: (oneShare * 0.997)
  const oneShareAfterFee = oneShare.mul(feeMultiplier).div(ethers.utils.parseUnits("1", 6));

  // Calculate sell fee
  const amountAfterSellFee = oneShare.add(3000);

  // Create token addresses
  const tokenAddress = determineAddress(co);

  // Create pool key for the token pair
  const poolKey = {
    currency0: tokenAddress.toLowerCase() < USDC_ADDRESS.toLowerCase() ? tokenAddress : USDC_ADDRESS,
    currency1: tokenAddress.toLowerCase() < USDC_ADDRESS.toLowerCase() ? USDC_ADDRESS : tokenAddress,
    fee: 3000,
    tickSpacing: 60,
    hooks: "0x0000000000000000000000000000000000000000"
  };

  // Determine if token is token0 or token1
  const tokenIsToken0 = tokenAddress.toLowerCase() === poolKey.currency0.toLowerCase();

  try {
    // For sell quote: we're selling token for USDC (exact input)
    const quoteSellResult = await quoterContract.callStatic.quoteExactInputSingle({
      poolKey: poolKey,
      zeroForOne: tokenIsToken0, // If token is token0, we're going from token0 to token1
      exactAmount: amountAfterSellFee,
      hookData: "0x"
    });

    const readableSellQuote = quoteSellResult.amountOut;
    objToReturn.sell = ethers.utils.formatUnits(readableSellQuote, 6);
  } catch (error) {
    console.warn(`Error Quote Sell: ${co.title} - No cash available in pool`);
    console.error("Sell quote error details:", error);
    objToReturn.sell = "-1";
  }

  try {
    // For buy quote: we want exactly 1 token, how much USDC do we need (exact output)
    const quoteBuyResult = await quoterContract.callStatic.quoteExactOutputSingle({
      poolKey: poolKey,
      zeroForOne: !tokenIsToken0, // Opposite direction from sell
      exactAmount: oneShare,
      hookData: "0x"
    });

    const readableBuyQuote = quoteBuyResult.amountIn;
    const priceWithFee = readableBuyQuote.mul(997).div(1000);
    objToReturn.buy = ethers.utils.formatUnits(priceWithFee, 6);
    return objToReturn;
  } catch (error) {
    console.warn(`Error Quote Buy: ${co.title} is Sold Out`);
    console.error("Buy quote error details:", error);
    objToReturn.buy = "-1";
    return objToReturn;
  }
}

export const fetchAllocations = async (
  ethersProvider: ethers.providers.Web3Provider,
  setPrimaryCompanyData: React.Dispatch<React.SetStateAction<PrimaryInvestmentType[]>>,
  setPrimaryCompanyDataLoaded: React.Dispatch<React.SetStateAction<boolean>>,
  smartWalletAddress: string
) => {
  if (!ethersProvider || !ethersProvider._isProvider) {
    return;
  }
  try {
    const modifiedInvestments = await Promise.all(
      PrimaryInvestments.map(async (option) => {
        if (option.wallet_address) {
          const contract = new ethers.Contract(option.wallet_address, deposit_ABI, ethersProvider);
          const allocationRaw = await contract.allocationAmount();
          const allocationValue = ethers.utils.formatUnits(allocationRaw, 6);
          const goal = parseFloat(allocationValue);
          const userDepositRaw = await contract.getUserBalance(smartWalletAddress);
          const userDepositValue = ethers.utils.formatUnits(userDepositRaw, 6);
          const userDeposit = parseFloat(userDepositValue);
          const usdcDepositRaw = await contract.totalDeposited();
          const usdcDepositValue = ethers.utils.formatUnits(usdcDepositRaw, 6);
          const deposited = parseFloat(usdcDepositValue);
          return { ...option, goal, deposited, userDeposit };
        }
        return option;
      })
    );
    setPrimaryCompanyData(modifiedInvestments);
  } catch (error) {
    if (!(error instanceof Error) || !error.message.includes("invalid provider")) {
      console.error("Critical error in fetchAllocations:", error);
    }
  } finally {
    setPrimaryCompanyDataLoaded(true);
  }
};
